import { useState } from 'react'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { Lightbox } from '@shared/units/Lightbox'
import { isYouTubeUrl, getYouTubeThumbnail } from '@shared/utils/videoUtils'
import GuardianExpoSubPageLayout from '../components/GuardianExpoSubPageLayout'
import SessionScheduleSection from '../components/SessionScheduleSection'
import StampRallySection from '../components/StampRallySection'
import MarketMapSection from '../components/MarketMapSection'
import ActivityVideosSection from '../components/ActivityVideosSection'
import PhotoGridSection from '../components/PhotoGridSection'
import ExpoVideoModal from '../components/ExpoVideoModal'
import {
  marketScheduleDays,
  stampConditions,
  redeemTimeParts,
  marketRuleImages,
} from '../data/marketSchedule'
import { marketMapBooths, marketMapLabels } from '../data/marketMapBooths'
import { useExpoMedia } from '../hooks/useExpoMedia'
import { EXPO_EVENT } from '../api/guardianExpoService'

const EVENT_TIME_IMAGE = '/image/guardian-expo/event_time.png'
const MARKET_MAP_IMAGE = '/image/guardian-expo/map_frame.png'

// 規則說明長圖（R1-R5）自成一組，Lightbox 只在這組內左右切換
const ruleImageSrcs = marketRuleImages.map((image) => image.src)

export default function GuardianExpoMarketPage() {
  const { t } = useLanguage()
  const { videos, photos } = useExpoMedia(EXPO_EVENT.MARKET)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [ruleLightboxIndex, setRuleLightboxIndex] = useState(null)
  const [zoomImageIndex, setZoomImageIndex] = useState(null)

  return (
    <GuardianExpoSubPageLayout
      bannerSrc="/image/guardian-expo/banner_market_desktop.webp"
      bannerAlt={t('guardianExpo.market.pageTitle')}
      content={t('guardianExpo.market.introContent')}
    >
      <SessionScheduleSection
        titleBlack={t('guardianExpo.market.scheduleTitleBlack')}
        titleRed={t('guardianExpo.market.scheduleTitleRed')}
        days={marketScheduleDays}
        columnLabels={{ session: t('guardianExpo.market.scheduleColumnSession'), time: t('guardianExpo.market.scheduleColumnTime') }}
        first
      />

      <StampRallySection
        titleBlack={t('guardianExpo.market.stampRallyTitleBlack')}
        titleRed={t('guardianExpo.market.stampRallyTitleRed')}
        conditionsLabel={t('guardianExpo.market.conditionsLabel')}
        conditions={stampConditions}
        redeemTimeLabel={t('guardianExpo.market.redeemTimeLabel')}
        redeemTimeParts={redeemTimeParts}
        eventTimeImageSrc={EVENT_TIME_IMAGE}
        eventTimeImageAlt={t('guardianExpo.market.redeemTimeLabel')}
        fastPassLabel={t('guardianExpo.market.fastPassLabel')}
        ruleImages={marketRuleImages}
        onRuleImageClick={(idx) => setRuleLightboxIndex(idx)}
      />

      <MarketMapSection
        titleBlack={t('guardianExpo.market.mapTitleBlack')}
        titleRed={t('guardianExpo.market.mapTitleRed')}
        mapSrc={MARKET_MAP_IMAGE}
        mapAlt={t('guardianExpo.market.mapTitleBlack') + t('guardianExpo.market.mapTitleRed')}
        booths={marketMapBooths}
        labels={marketMapLabels}
      />

      {videos.length > 0 && <ActivityVideosSection
        videos={videos.map((video) => ({
          id: video.expo_media_id,
          thumbnail: video.media_url && isYouTubeUrl(video.media_url) ? getYouTubeThumbnail(video.media_url) : undefined,
          title: video.title,
          onClick: () => setSelectedVideo(video),
        }))}
      />}

      {photos.length > 0 && <PhotoGridSection
        titleBlack={t('guardianExpo.market.photosTitleBlack')}
        titleRed={t('guardianExpo.market.photosTitleRed')}
        photos={photos.map((photo) => ({ id: photo.expo_media_id, src: photo.media_url, alt: photo.title }))}
        onPhotoClick={(idx) => setZoomImageIndex(idx)}
        last
      />}

      <ExpoVideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />

      {ruleLightboxIndex !== null && (
        <Lightbox
          images={ruleImageSrcs}
          index={ruleLightboxIndex}
          onIndexChange={setRuleLightboxIndex}
          onClose={() => setRuleLightboxIndex(null)}
        />
      )}

      {zoomImageIndex !== null && (
        <Lightbox
          images={photos.map((photo) => photo.media_url)}
          index={zoomImageIndex}
          onIndexChange={setZoomImageIndex}
          onClose={() => setZoomImageIndex(null)}
        />
      )}
    </GuardianExpoSubPageLayout>
  )
}
